/**
 * cues-ship.ts — procedural ship-lifecycle SFX builders (v2M8.5).
 *
 * Implements AUDIO §7 + §8 lifecycle cues. Each is paired 1:1 with its
 * v2M7 VFX counterpart so audio and visuals land on the same frame:
 *
 *   - **ship:destroyed**       (§8) ↔ v2M7.5 destruction sequence (10 ticks)
 *   - **ship:hyperspace-out**  (§7) ↔ v2M7.6 jump-out (17 ticks)
 *   - **ship:hyperspace-in**   (§7) ↔ v2M7.6 jump-in (6 ticks)
 *   - **ship:cloak-engage**    (§7) ↔ v2M7's cloak-engage shimmer fade
 *   - **ship:cloak-disengage** (§7) ↔ v2M7's cloak-disengage reveal
 *   - **ship:throttle-up**     (§7) — fires when a ship accepts a move
 *                                     order; no VFX counterpart, audio only.
 *
 * All six route through the SFX bus and respect AUDIO §9.1 positional
 * pan + attenuation supplied by `diff.ts` via `PlayCueOpts`. They also
 * accept `pitchMul` / `gainMul` variation per AUDIO §6.1, although the
 * v2M8.5 detection path only stamps neutral values for these — ship
 * lifecycle events are uncommon enough that machine-gun-fatigue isn't
 * a concern.
 *
 * Engine idle / throttle-down loops, looping ambient cues, and per-
 * chassis destruction variants (light/medium/heavy) deferred to v3M11
 * polish — looped + per-ship-instance state is meaningfully more infra
 * than the diff path.
 */

import type { AudioEngine } from './engine';
import type { CueVariation } from './registry';

const NEUTRAL: CueVariation = { pitchMul: 1, gainMul: 1 };

export function registerShipCues(engine: AudioEngine): void {
  engine.registry.registerProcedural('ship:destroyed', 'sfx', buildShipDestroyed);
  engine.registry.registerProcedural('ship:hyperspace-out', 'sfx', buildHyperspaceOut);
  engine.registry.registerProcedural('ship:hyperspace-in', 'sfx', buildHyperspaceIn);
  engine.registry.registerProcedural('ship:cloak-engage', 'sfx', buildCloakEngage);
  engine.registry.registerProcedural('ship:cloak-disengage', 'sfx', buildCloakDisengage);
  engine.registry.registerProcedural('ship:throttle-up', 'sfx', buildThrottleUp);
}

/**
 * Ship destroyed — explosive transient + debris scatter tail, 1 s.
 * Three layered sources: a sub-bass thump for the initial blast, a
 * broadband noise burst for the explosion body, and a high-passed
 * noise tail descending in volume + frequency for the debris scatter.
 */
function buildShipDestroyed(
  ctx: AudioContext,
  target: AudioNode,
  variation: CueVariation = NEUTRAL,
): AudioScheduledSourceNode {
  const now = ctx.currentTime;
  // Sub-bass thump — sine sweep 80 → 30 Hz, fast attack + long decay.
  const thump = ctx.createOscillator();
  thump.type = 'sine';
  thump.frequency.setValueAtTime(80 * variation.pitchMul, now);
  thump.frequency.exponentialRampToValueAtTime(30 * variation.pitchMul, now + 0.4);
  const thumpGain = ctx.createGain();
  thumpGain.gain.setValueAtTime(0.0001, now);
  thumpGain.gain.exponentialRampToValueAtTime(0.5 * variation.gainMul, now + 0.005);
  thumpGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.5);
  thump.connect(thumpGain);
  thumpGain.connect(target);
  thump.start(now);
  thump.stop(now + 0.51);
  // Explosion body — broadband noise, lowpassed, fast attack quick decay.
  const body = createNoiseSource(ctx, 0.4);
  const bodyFilter = ctx.createBiquadFilter();
  bodyFilter.type = 'lowpass';
  bodyFilter.frequency.setValueAtTime(2400, now);
  bodyFilter.frequency.exponentialRampToValueAtTime(400, now + 0.35);
  const bodyGain = ctx.createGain();
  bodyGain.gain.setValueAtTime(0.0001, now);
  bodyGain.gain.exponentialRampToValueAtTime(0.4 * variation.gainMul, now + 0.005);
  bodyGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.4);
  body.connect(bodyFilter);
  bodyFilter.connect(bodyGain);
  bodyGain.connect(target);
  body.start(now);
  body.stop(now + 0.41);
  // Debris scatter tail — high-passed noise lingering for 1 s, fading
  // with random "ping" texture from a band-pass at ~2 kHz.
  const tail = createNoiseSource(ctx, 1.0);
  const tailFilter = ctx.createBiquadFilter();
  tailFilter.type = 'bandpass';
  tailFilter.frequency.value = 2000;
  tailFilter.Q.value = 4;
  const tailGain = ctx.createGain();
  tailGain.gain.setValueAtTime(0.0001, now + 0.05);
  tailGain.gain.exponentialRampToValueAtTime(0.18 * variation.gainMul, now + 0.1);
  tailGain.gain.exponentialRampToValueAtTime(0.0001, now + 1.0);
  tail.connect(tailFilter);
  tailFilter.connect(tailGain);
  tailGain.connect(target);
  tail.start(now);
  tail.stop(now + 1.01);
  return thump;
}

/**
 * Hyperspace out — rising tone + whoosh collapse, 1 s. Tone climbs
 * 200 → 1500 Hz over 700 ms then collapses to silence; whoosh is a
 * band-pass-filtered noise tracking the same sweep.
 */
function buildHyperspaceOut(
  ctx: AudioContext,
  target: AudioNode,
  variation: CueVariation = NEUTRAL,
): AudioScheduledSourceNode {
  const now = ctx.currentTime;
  const tone = ctx.createOscillator();
  tone.type = 'sine';
  tone.frequency.setValueAtTime(200 * variation.pitchMul, now);
  tone.frequency.exponentialRampToValueAtTime(1500 * variation.pitchMul, now + 0.7);
  // Collapse — frequency rams to 0 in the last 200 ms, simulating the
  // bright dot fading.
  tone.frequency.exponentialRampToValueAtTime(50 * variation.pitchMul, now + 0.95);
  const toneGain = ctx.createGain();
  toneGain.gain.setValueAtTime(0.0001, now);
  toneGain.gain.exponentialRampToValueAtTime(0.3 * variation.gainMul, now + 0.05);
  toneGain.gain.setValueAtTime(0.3 * variation.gainMul, now + 0.7);
  toneGain.gain.exponentialRampToValueAtTime(0.0001, now + 1.0);
  tone.connect(toneGain);
  toneGain.connect(target);
  tone.start(now);
  tone.stop(now + 1.01);
  // Whoosh — band-pass noise tracking the tone.
  const whoosh = createNoiseSource(ctx, 1.0);
  const whooshFilter = ctx.createBiquadFilter();
  whooshFilter.type = 'bandpass';
  whooshFilter.Q.value = 5;
  whooshFilter.frequency.setValueAtTime(300 * variation.pitchMul, now);
  whooshFilter.frequency.exponentialRampToValueAtTime(2500 * variation.pitchMul, now + 0.7);
  const whooshGain = ctx.createGain();
  whooshGain.gain.setValueAtTime(0.0001, now);
  whooshGain.gain.exponentialRampToValueAtTime(0.2 * variation.gainMul, now + 0.1);
  whooshGain.gain.setValueAtTime(0.2 * variation.gainMul, now + 0.7);
  whooshGain.gain.exponentialRampToValueAtTime(0.0001, now + 1.0);
  whoosh.connect(whooshFilter);
  whooshFilter.connect(whooshGain);
  whooshGain.connect(target);
  whoosh.start(now);
  whoosh.stop(now + 1.01);
  return tone;
}

/**
 * Hyperspace in — reverse of hyperspace out. Tone descends 1500 →
 * 200 Hz, whoosh sweeps high → low, gain ramps in over 200 ms then
 * tapers to settled-state silence.
 */
function buildHyperspaceIn(
  ctx: AudioContext,
  target: AudioNode,
  variation: CueVariation = NEUTRAL,
): AudioScheduledSourceNode {
  const now = ctx.currentTime;
  const tone = ctx.createOscillator();
  tone.type = 'sine';
  tone.frequency.setValueAtTime(50 * variation.pitchMul, now);
  tone.frequency.exponentialRampToValueAtTime(1500 * variation.pitchMul, now + 0.2);
  tone.frequency.exponentialRampToValueAtTime(200 * variation.pitchMul, now + 0.9);
  const toneGain = ctx.createGain();
  toneGain.gain.setValueAtTime(0.0001, now);
  toneGain.gain.exponentialRampToValueAtTime(0.3 * variation.gainMul, now + 0.2);
  toneGain.gain.setValueAtTime(0.3 * variation.gainMul, now + 0.5);
  toneGain.gain.exponentialRampToValueAtTime(0.0001, now + 1.0);
  tone.connect(toneGain);
  toneGain.connect(target);
  tone.start(now);
  tone.stop(now + 1.01);
  // Whoosh tracks the tone in reverse.
  const whoosh = createNoiseSource(ctx, 1.0);
  const whooshFilter = ctx.createBiquadFilter();
  whooshFilter.type = 'bandpass';
  whooshFilter.Q.value = 5;
  whooshFilter.frequency.setValueAtTime(2500 * variation.pitchMul, now);
  whooshFilter.frequency.exponentialRampToValueAtTime(300 * variation.pitchMul, now + 0.9);
  const whooshGain = ctx.createGain();
  whooshGain.gain.setValueAtTime(0.0001, now);
  whooshGain.gain.exponentialRampToValueAtTime(0.2 * variation.gainMul, now + 0.15);
  whooshGain.gain.setValueAtTime(0.2 * variation.gainMul, now + 0.6);
  whooshGain.gain.exponentialRampToValueAtTime(0.0001, now + 1.0);
  whoosh.connect(whooshFilter);
  whooshFilter.connect(whooshGain);
  whooshGain.connect(target);
  whoosh.start(now);
  whoosh.stop(now + 1.01);
  return tone;
}

/**
 * Cloak engage — descending whisper + pitch warble, 1 s. Filtered
 * noise (whisper texture) with descending centre frequency and a
 * sub-Hz LFO modulating an oscillator for the warble overtone.
 */
function buildCloakEngage(
  ctx: AudioContext,
  target: AudioNode,
  variation: CueVariation = NEUTRAL,
): AudioScheduledSourceNode {
  const now = ctx.currentTime;
  // Whisper — band-pass noise descending centre frequency.
  const whisper = createNoiseSource(ctx, 1.0);
  const whisperFilter = ctx.createBiquadFilter();
  whisperFilter.type = 'bandpass';
  whisperFilter.Q.value = 4;
  whisperFilter.frequency.setValueAtTime(1800 * variation.pitchMul, now);
  whisperFilter.frequency.exponentialRampToValueAtTime(400 * variation.pitchMul, now + 0.95);
  const whisperGain = ctx.createGain();
  whisperGain.gain.setValueAtTime(0.0001, now);
  whisperGain.gain.exponentialRampToValueAtTime(0.18 * variation.gainMul, now + 0.05);
  whisperGain.gain.exponentialRampToValueAtTime(0.0001, now + 1.0);
  whisper.connect(whisperFilter);
  whisperFilter.connect(whisperGain);
  whisperGain.connect(target);
  whisper.start(now);
  whisper.stop(now + 1.01);
  // Warble — LFO-modulated triangle.
  const warble = ctx.createOscillator();
  warble.type = 'triangle';
  warble.frequency.value = 380 * variation.pitchMul;
  const lfo = ctx.createOscillator();
  lfo.type = 'sine';
  lfo.frequency.value = 6;
  const lfoGain = ctx.createGain();
  lfoGain.gain.value = 60 * variation.pitchMul;
  lfo.connect(lfoGain);
  lfoGain.connect(warble.frequency);
  const warbleGain = ctx.createGain();
  warbleGain.gain.setValueAtTime(0.0001, now);
  warbleGain.gain.exponentialRampToValueAtTime(0.12 * variation.gainMul, now + 0.1);
  warbleGain.gain.exponentialRampToValueAtTime(0.0001, now + 1.0);
  warble.connect(warbleGain);
  warbleGain.connect(target);
  warble.start(now);
  warble.stop(now + 1.01);
  lfo.start(now);
  lfo.stop(now + 1.01);
  return whisper;
}

/**
 * Cloak disengage — rising reveal, 500 ms. Like cloak-engage in
 * reverse but compressed: rising filtered-noise sweep, brief warble.
 */
function buildCloakDisengage(
  ctx: AudioContext,
  target: AudioNode,
  variation: CueVariation = NEUTRAL,
): AudioScheduledSourceNode {
  const now = ctx.currentTime;
  const whisper = createNoiseSource(ctx, 0.5);
  const whisperFilter = ctx.createBiquadFilter();
  whisperFilter.type = 'bandpass';
  whisperFilter.Q.value = 4;
  whisperFilter.frequency.setValueAtTime(400 * variation.pitchMul, now);
  whisperFilter.frequency.exponentialRampToValueAtTime(2200 * variation.pitchMul, now + 0.45);
  const whisperGain = ctx.createGain();
  whisperGain.gain.setValueAtTime(0.0001, now);
  whisperGain.gain.exponentialRampToValueAtTime(0.22 * variation.gainMul, now + 0.15);
  whisperGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.5);
  whisper.connect(whisperFilter);
  whisperFilter.connect(whisperGain);
  whisperGain.connect(target);
  whisper.start(now);
  whisper.stop(now + 0.51);
  return whisper;
}

/**
 * Throttle-up — rising synth sweep, 400 ms. Sawtooth with a low-pass
 * filter sweeping in lockstep so the brightness rises with frequency.
 */
function buildThrottleUp(
  ctx: AudioContext,
  target: AudioNode,
  variation: CueVariation = NEUTRAL,
): AudioScheduledSourceNode {
  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(120 * variation.pitchMul, now);
  osc.frequency.exponentialRampToValueAtTime(420 * variation.pitchMul, now + 0.35);
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.Q.value = 2;
  filter.frequency.setValueAtTime(600, now);
  filter.frequency.exponentialRampToValueAtTime(2400, now + 0.35);
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(0.18 * variation.gainMul, now + 0.04);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.4);
  osc.connect(filter);
  filter.connect(gain);
  gain.connect(target);
  osc.start(now);
  osc.stop(now + 0.41);
  return osc;
}

function createNoiseSource(ctx: AudioContext, durationSec: number): AudioBufferSourceNode {
  const length = Math.max(1, Math.floor(ctx.sampleRate * durationSec));
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  return source;
}
